import React from "react";
import { useState } from "react";
import DoubleClickEdit from "../../../../../components/DoubleClickEdit";
import { BsArrowClockwise } from "react-icons/bs";
import { STATUS_OPTIONS } from "../../../../../constants";
import { XCircleIcon } from "@heroicons/react/solid";

/**
 * Inline double-click-editable repeat-frequency selector for a task.
 * In client-portal mode renders a read-only label (only shown when a
 * frequency is set). In advisor mode provides a dropdown to choose from
 * Never, Daily, Weekly, Monthly, Quarterly, Bi-Yearly, and Yearly.
 * Selecting a value immediately calls `handleSubmitEdit`.
 *
 * @param {object}   props
 * @param {Function} props.handleSubmitEdit - Called with the new frequency string (or null) on change
 * @param {boolean}  props.isClientPortal   - Renders read-only client-portal view when true
 * @param {object}   props.task             - Task object; `task.repeat_frequency` seeds the initial value
 */
const RepeatFrequency = ({ handleSubmitEdit, isClientPortal, task }) => {
    const [value, setValue] = useState(task.repeat_frequency);
    const [showEdit, setShowEdit] = useState(false);

    const handleChange = (e) => {
        // React drops `value={null}` from the DOM, so selecting "Never"
        // surfaces the option text ("Never") as e.target.value. Normalize
        // it to null before sending — the server only accepts the six
        // frequency strings or null.
        const raw = e.target.value;
        const newValue = !raw || raw === "Never" ? null : raw;
        setValue(newValue);
        handleSubmitEdit(newValue);
        setShowEdit(false);
    };

    const handleClearValue = () => {
        setValue(null);
        handleSubmitEdit(null);
        setShowEdit(false);
    }

    const repeatOptions = [
        { value: null, label: "Never" },
        { value: "daily", label: "Daily" },
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" },
        { value: "quarterly", label: "Quarterly" },
        { value: "bi-yearly", label: "Bi-Yearly" },
        { value: "yearly", label: "Yearly" },
    ]
    return (
        <>


            {isClientPortal ?
                <div className="flex items-center text-xs text-gray-500">
                    {task?.repeat_frequency ? (
                        <div className="flex items-center gap-1">
                            <BsArrowClockwise
                                className="mr-1 h-3.5 w-3.5 flex-shrink-0 text-gray-400"
                                aria-hidden="true"
                            />
                            <span className="capitalize">{value}</span>
                        </div>
                    ) : null}
                </div> :
                <DoubleClickEdit
                    showEdit={showEdit}
                    setShowEdit={setShowEdit}
                    defaultComponent={
                        <div className="flex items-center text-xs text-gray-500">
                            <BsArrowClockwise
                                className="mr-1 h-3.5 w-3.5 flex-shrink-0 text-gray-400"
                                aria-hidden="true"
                            />
                            {value ? (
                                <div className="group flex items-center gap-1">
                                    <span className="capitalize">{value}</span>
                                    <button onClick={handleClearValue} aria-label="Clear repeat frequency" className="invisible group-hover:visible">
                                        <XCircleIcon className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                                    </button>
                                </div>
                            ) : (
                                <span className="min-w-max">No repeat</span>
                            )}
                        </div>
                    }
                    editComponent={
                        <select
                            name="repeat-input"
                            id="repeat-input"
                            className="input-primary"
                            onChange={handleChange}
                        >
                            <option hidden value=""></option>
                            {repeatOptions.map((option) => (
                                <option value={option.value} key={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    }
                    handleSubmitEdit={() => { }}
                    componentName="repeat_frequency"
                />}
        </>
    );
};

export default RepeatFrequency; 